import * as THREE from 'three';
import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import type { PlayerService } from '../player/playerSystem';

/**
 * Occlusion silhouette (docs/MOBILE.md "Camera on a phone"): where a building, a roof or a tree hides the
 * player or the car they drive, a flat tinted silhouette shows through, so a car parked tight against a
 * frontage on the camera side never just vanishes. The city's cut-away (world/view/occluderFade.ts) opens a
 * hole in the merged city materials along the sight line; this covers what that cannot reach (the detailed
 * model packs, props, anything with its own material) and the car's far corners outside the hole.
 *
 * How: each mesh of the player figure and of the car body gets a child drawing the same geometry (the
 * skinned ones bound to the same skeleton) with a flat material whose depth test is GREATER, so only the
 * fragments *behind* something already drawn pass. The silhouette's vertices are pulled towards the camera
 * along the view ray in the vertex shader, which leaves the screen shape unchanged but moves its depth in
 * front of the figure's own back and inner surfaces (a car's seats behind its roof would otherwise show as
 * tint all the time). So only an occluder more than `pull` metres in front of the surface lets the tint
 * through. Cost: one extra draw per silhouetted mesh (two or three a frame), early-z rejected wherever the
 * player is in plain view; no extra pass, no stencil, every tier.
 */
const TINT = 0xf2dcae;
const OPACITY = 0.58;
/** Pull towards the camera (m): more than the thickness of the figure / car along the view ray. */
const PULL_FIGURE = 1.1;
const PULL_CAR = 2.6;

function silhouetteMaterial(pull: number): THREE.MeshBasicMaterial {
  const m = new THREE.MeshBasicMaterial({ color: TINT, transparent: true, opacity: OPACITY, depthFunc: THREE.GreaterDepth, depthWrite: false, fog: false });
  m.name = 'occlusion-silhouette';
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uPull = { value: pull };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uPull;')
      .replace(
        '#include <project_vertex>',
        `#include <project_vertex>
        // Along the view ray towards the camera: same pixel, nearer depth.
        float vd = length(mvPosition.xyz);
        mvPosition.xyz -= mvPosition.xyz / max(vd, 1e-3) * min(uPull, vd * 0.5);
        gl_Position = projectionMatrix * mvPosition;`,
      );
  };
  m.customProgramCacheKey = () => `occlusion-silhouette`;
  return m;
}

interface Shadowed {
  source: THREE.Mesh;
  copy: THREE.Mesh;
}

/** A child of `source` drawing its geometry as a silhouette (skinned meshes share the skeleton). */
function addSilhouette(source: THREE.Mesh, material: THREE.Material): Shadowed {
  let copy: THREE.Mesh;
  const skinned = source as THREE.SkinnedMesh;
  if (skinned.isSkinnedMesh) {
    const s = new THREE.SkinnedMesh(source.geometry, material);
    s.bind(skinned.skeleton, skinned.bindMatrix);
    if (skinned.boundingSphere) s.boundingSphere = skinned.boundingSphere;
    copy = s;
  } else {
    copy = new THREE.Mesh(source.geometry, material);
  }
  copy.name = 'silhouette';
  copy.frustumCulled = source.frustumCulled;
  copy.renderOrder = 4;
  copy.castShadow = false;
  copy.receiveShadow = false;
  source.add(copy);
  return { source, copy };
}

function removeSilhouette(s: Shadowed): void {
  s.copy.removeFromParent();
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Review aid: `true` draws the silhouettes over everything (the tint should cover the figure / car exactly). */
    silhouetteDebug(on: boolean): void;
  }
}

export function createOcclusionSilhouetteSystem(): System {
  let ctx: GameContext;
  let player: PlayerService | null = null;
  const figureMat = silhouetteMaterial(PULL_FIGURE);
  const carMat = silhouetteMaterial(PULL_CAR);
  const figure: Shadowed[] = [];
  let car: Shadowed | null = null;
  let carId: number | null = null;
  let sinceScan = 1;

  /** The player figure's meshes: the rig body at first, the detailed figure once it is in, the weapon in hand. */
  function scanFigure(root: THREE.Object3D): void {
    for (let i = figure.length - 1; i >= 0; i--) {
      const s = figure[i]!;
      if (!s.source.parent) {
        removeSilhouette(s);
        figure.splice(i, 1);
      }
    }
    root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || m.name === 'silhouette' || figure.some((s) => s.source === m)) return;
      figure.push(addSilhouette(m, figureMat));
    });
  }

  return {
    id: 'occlusion-silhouette',
    order: 712,
    init(c) {
      ctx = c;
      player = c.services.tryGet('player') ?? null;
      c.hooks.register('silhouetteDebug', (on) => {
        for (const m of [figureMat, carMat]) {
          m.depthFunc = on ? THREE.AlwaysDepth : THREE.GreaterDepth;
          m.needsUpdate = true;
        }
      });
    },
    update(dt) {
      if (!ctx.render || !player) return;
      sinceScan += dt;
      const rig = ctx.services.tryGet('playerRig');
      if (rig && sinceScan > 0.5) {
        sinceScan = 0;
        scanFigure(rig.root);
      }
      // The car: its body mesh while we drive it (the view builds the rig; find it by name once per car).
      const driving = player.mode === 'driving' || player.mode === 'riding';
      const want = driving ? (player.vehicle?.id ?? null) : null;
      if (car && (car.source.parent === null || want !== carId)) {
        removeSilhouette(car);
        car = null;
      }
      if (want !== null && !car) {
        const root = ctx.scene.getObjectByName(`vehicle-${want}`);
        const body = root?.getObjectByName('body') as THREE.Mesh | undefined;
        if (body?.isMesh) car = addSilhouette(body, carMat);
      }
      carId = want;
      // The body geometry is swapped for LOD and dents: draw whatever it shows now.
      if (car) car.copy.geometry = car.source.geometry;
    },
    dispose() {
      for (const s of figure) removeSilhouette(s);
      figure.length = 0;
      if (car) removeSilhouette(car);
      car = null;
      figureMat.dispose();
      carMat.dispose();
    },
  };
}
